import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateYandexAppDto,
  SetYandexCampaignBranchDto,
  MetrikaCounterView,
  RunYandexSyncDto,
  StartYandexConnectionDto,
  UpdateMetrikaCounterDto,
  UpdateYandexAppDto,
  UpdateYandexConnectionDto,
  UpdateYandexDirectAccountDto,
  UpdateYandexSettingsDto,
  YandexAppView,
  YandexAuthSessionView,
  YandexCampaignView,
  YandexConnectionView,
  YandexDirectAccountView,
  YandexIntegrationView,
  YandexSettingsView,
  YandexSyncJobView,
  YandexSyncStatusView,
} from '@ads-manager/shared';
import { yandexApi, OWN_ACCOUNTS_SCOPE } from '../api';
import { QUERY_KEYS, STALE_TIMES } from './query-keys';

/** Как часто спрашиваем бэкенд «код уже подтвердили?». */
const AUTH_POLL_INTERVAL_MS = 2000;

const settingsKey = [QUERY_KEYS.YANDEX_SETTINGS] as const;
const integrationKey = [QUERY_KEYS.YANDEX_INTEGRATION] as const;
const appsKey = [QUERY_KEYS.YANDEX_APPS] as const;
const syncStatusKey = [QUERY_KEYS.YANDEX_SYNC_STATUS] as const;

/** Пока задача идёт, состояние опрашивается часто; в покое — редко. */
const SYNC_POLL_ACTIVE_MS = 5000;
const SYNC_POLL_IDLE_MS = 60_000;

// --------------------------------------------------------- очередь выкачки

/**
 * Состояние очереди выкачки. Опрос учащается, пока задача идёт: по нему
 * страница «Интеграции» рисует прогресс, а отчёт — «данные обновляются».
 */
export function useYandexSyncStatus(enabled = true) {
  return useQuery<YandexSyncStatusView>({
    queryKey: syncStatusKey,
    queryFn: yandexApi.getYandexSyncStatus,
    enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.active || data.queued > 0)
        ? SYNC_POLL_ACTIVE_MS
        : SYNC_POLL_IDLE_MS;
    },
  });
}

export function useRunYandexSync() {
  const queryClient = useQueryClient();
  return useMutation<YandexSyncJobView, Error, RunYandexSyncDto>({
    mutationFn: yandexApi.runYandexSync,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: syncStatusKey }),
  });
}

export function useStartYandexBackfill() {
  const queryClient = useQueryClient();
  return useMutation<YandexSyncJobView, Error, void>({
    mutationFn: yandexApi.startYandexBackfill,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: syncStatusKey }),
  });
}

/**
 * Ключ списка подключений. `undefined` — все доступные пользователю,
 * `own` — подключения заказчика, иначе конкретный канал подрядчика.
 */
export const connectionsKey = (contractorChannelId?: string) =>
  [QUERY_KEYS.YANDEX_CONNECTIONS, contractorChannelId ?? 'all'] as const;

// --------------------------------------------------------------- настройки

/** Привязка интеграции к каналу трафика (только трафик-менеджеру). */
export function useYandexSettings(enabled = true) {
  return useQuery({
    queryKey: settingsKey,
    queryFn: yandexApi.getYandexSettings,
    staleTime: STALE_TIMES.SEMI_STATIC,
    enabled,
  });
}

export function useUpdateYandexSettings() {
  const queryClient = useQueryClient();
  return useMutation<YandexSettingsView, Error, UpdateYandexSettingsDto>({
    mutationFn: yandexApi.updateYandexSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKey, data);
      // Смена привязки меняет список каналов у всех, кто его видит.
      queryClient.invalidateQueries({ queryKey: integrationKey });
    },
  });
}

/**
 * Что доступно текущему пользователю: привязка и его каналы.
 * Пустой `channels` у подрядчика значит «вкладку „Интеграции“ не показывать».
 */
export function useYandexIntegration(enabled = true) {
  return useQuery<YandexIntegrationView>({
    queryKey: integrationKey,
    queryFn: yandexApi.getYandexIntegration,
    staleTime: STALE_TIMES.SEMI_STATIC,
    enabled,
  });
}

// -------------------------------------------------------------- приложения

export function useYandexApps(enabled = true) {
  return useQuery({
    queryKey: appsKey,
    queryFn: yandexApi.getYandexApps,
    staleTime: STALE_TIMES.SEMI_STATIC,
    enabled,
  });
}

export function useCreateYandexApp() {
  const queryClient = useQueryClient();
  return useMutation<YandexAppView, Error, CreateYandexAppDto>({
    mutationFn: yandexApi.createYandexApp,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: appsKey }),
  });
}

export function useUpdateYandexApp() {
  const queryClient = useQueryClient();
  return useMutation<
    YandexAppView,
    Error,
    { id: string; dto: UpdateYandexAppDto }
  >({
    mutationFn: ({ id, dto }) => yandexApi.updateYandexApp(id, dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: appsKey }),
  });
}

export function useDeleteYandexApp() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, Error, string>({
    mutationFn: yandexApi.deleteYandexApp,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: appsKey }),
  });
}

// ------------------------------------------------------------- подключения

/**
 * Подключения. `contractorChannelId` — конкретный канал подрядчика либо
 * `OWN_ACCOUNTS_SCOPE` для аккаунтов заказчика; без него — всё доступное.
 */
export function useYandexConnections(
  contractorChannelId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: connectionsKey(contractorChannelId),
    queryFn: () => yandexApi.getYandexConnections(contractorChannelId),
    staleTime: STALE_TIMES.DYNAMIC,
    enabled,
  });
}

/**
 * Путь к сквозной аналитике Яндекса для канала, выбранного подрядчиком в
 * шапке, или null, если отчёта строить не из чего.
 *
 * Правило то же, что у кнопки «Сводная аналитика» в `YandexAccountsPanel`:
 * за каким-нибудь подключением канала (живым, отозванным или отключённым)
 * нашёлся кабинет Директа или счётчик Метрики. Запросы те же — кэш общий.
 */
export function useContractorYandexAnalyticsPath(
  selectedTrafficChannelId: string | null | undefined,
  enabled: boolean
): string | null {
  const integrationQuery = useYandexIntegration(enabled);
  const channel = integrationQuery.data?.channels.find(
    (item) => item.trafficChannelId === selectedTrafficChannelId
  );
  const contractorChannelId = channel?.contractorChannelId;
  const connectionsQuery = useYandexConnections(
    contractorChannelId,
    enabled && Boolean(contractorChannelId)
  );

  if (!enabled || !contractorChannelId) return null;
  const hasData = (connectionsQuery.data ?? []).some(
    (connection) =>
      connection.directAccounts.length > 0 ||
      connection.metrikaCounters.length > 0
  );
  return hasData ? `/contractor/yandex-analytics/${contractorChannelId}` : null;
}

export function useStartYandexConnection() {
  return useMutation<YandexAuthSessionView, Error, StartYandexConnectionDto>({
    mutationFn: yandexApi.startYandexConnection,
  });
}

/**
 * Опрос статуса сессии авторизации.
 *
 * Опрашивается наш бэкенд, а не Яндекс: если ходить в Яндекс из браузера,
 * закрытая вкладка убьёт авторизацию на середине, а подрядчик код уже введёт.
 */
export function useYandexAuthSession(sessionId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.YANDEX_AUTH_SESSION, sessionId] as const,
    queryFn: () => yandexApi.getYandexAuthSession(sessionId as string),
    enabled: Boolean(sessionId),
    // Опрос останавливается, как только сессия перестала быть PENDING.
    refetchInterval: (query) =>
      query.state.data?.status === 'PENDING' ? AUTH_POLL_INTERVAL_MS : false,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useVerifyYandexConnection() {
  const queryClient = useQueryClient();
  return useMutation<YandexConnectionView, Error, string>({
    mutationFn: yandexApi.verifyYandexConnection,
    onSuccess: () => invalidateConnections(queryClient),
  });
}

export function useReauthYandexConnection() {
  return useMutation<YandexAuthSessionView, Error, string>({
    mutationFn: yandexApi.reauthYandexConnection,
  });
}

export function useUpdateYandexConnection() {
  const queryClient = useQueryClient();
  return useMutation<
    YandexConnectionView,
    Error,
    { id: string; dto: UpdateYandexConnectionDto }
  >({
    mutationFn: ({ id, dto }) => yandexApi.updateYandexConnection(id, dto),
    onSuccess: () => invalidateConnections(queryClient),
  });
}

export function useDeleteYandexConnection() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, Error, string>({
    mutationFn: yandexApi.deleteYandexConnection,
    onSuccess: () => {
      invalidateConnections(queryClient);
      // Удаление освобождает приложение — счётчик подключений на нём меняется.
      queryClient.invalidateQueries({ queryKey: appsKey });
    },
  });
}

export function useUpdateYandexDirectAccount() {
  const queryClient = useQueryClient();
  return useMutation<
    YandexDirectAccountView,
    Error,
    { id: string; dto: UpdateYandexDirectAccountDto }
  >({
    mutationFn: ({ id, dto }) => yandexApi.updateYandexDirectAccount(id, dto),
    onSuccess: () => invalidateConnections(queryClient),
  });
}

export function useUpdateMetrikaCounter() {
  const queryClient = useQueryClient();
  return useMutation<
    MetrikaCounterView,
    Error,
    { id: string; dto: UpdateMetrikaCounterDto }
  >({
    mutationFn: ({ id, dto }) => yandexApi.updateMetrikaCounter(id, dto),
    onSuccess: () => invalidateConnections(queryClient),
  });
}

/**
 * Инвалидация всех срезов списка подключений сразу: один и тот же счётчик
 * Метрики виден из нескольких срезов, и обновлять только текущий — врать
 * остальным экранам.
 */
function invalidateConnections(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.YANDEX_CONNECTIONS] });
}

// ------------------------------------------------- кампании Директа

/**
 * Ручной филиал кампании без заявок — из строки отчёта по рекламе.
 *
 * Кэш здесь не трогается нарочно: отчёт на бэкенде кэшируется минуту, и
 * обычная инвалидация вернула бы тот же разрез. Страница после успеха сама
 * просит пересобрать отчёт мимо кэша (`rebuild`).
 */
export function useSetYandexCampaignBranch() {
  return useMutation<
    YandexCampaignView,
    Error,
    { id: string; dto: SetYandexCampaignBranchDto }
  >({
    mutationFn: ({ id, dto }) => yandexApi.setYandexCampaignBranch(id, dto),
  });
}

export { OWN_ACCOUNTS_SCOPE };
